import type { IncidentHourDto, UptimeHourDto } from '@/lib/dto'
import type { HourTrackPoint, IncidentKindCount } from '@/ui/charts/hour-tracks'

/**
 * Сборка суточного ряда для графика из двух массивов сводки.
 *
 * По контракту (docs/06 §9) `uptimeHours24[i].hour === incidentsHours24[i].hour`,
 * то есть сопоставлять ряды нужно ПО ИНДЕКСУ, а не по значению `hour`: ярлык
 * часа повторяется раз в сутки и на границе суток совпадёт с чужим сегментом.
 * Но полагаться на равные длины нельзя — старый API мог не отдать второй ряд
 * вовсе, и дашборд не должен из-за этого остаться без графика.
 */
export function mergeHourTracks(
  uptime: readonly UptimeHourDto[] = [],
  incidents: readonly IncidentHourDto[] = []
): HourTrackPoint[] {
  const length = Math.max(uptime.length, incidents.length)
  return Array.from({ length }, (_unused, index) => {
    const uptimeHour = uptime[index]
    const incidentHour = incidents[index]
    return {
      // Ярлык берём у того ряда, который есть; оба отсутствовать не могут.
      hour: uptimeHour?.hour ?? incidentHour?.hour ?? index,
      uptimePct: uptimeHour?.uptimePct ?? null,
      incidents: incidentHour?.count ?? 0,
      kinds: incidentHour === undefined ? [] : incidentKindsOf(incidentHour)
    }
  })
}

/**
 * Разбивка инцидентов часа по видам событий.
 *
 * Поле `kinds` добавлено в API позже (`apps/api/src/modules/dashboard`), а в
 * типах фронта (`lib/dto.ts`) его пока нет. Читаем защитно и проверяем форму в
 * рантайме, а не приводим типом: сводка приходит и от старого сервера, и тогда
 * тултип обязан просто остаться без разбивки, а не уронить экран.
 */
export function incidentKindsOf(hour: IncidentHourDto): IncidentKindCount[] {
  const raw: unknown = (hour as { kinds?: unknown }).kinds
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry): IncidentKindCount[] => {
    if (entry === null || typeof entry !== 'object') return []
    const { kind, count } = entry as { kind?: unknown; count?: unknown }
    if (typeof kind !== 'string' || kind === '' || typeof count !== 'number') return []
    return [{ kind, count }]
  })
}
